import { createTheme } from "@mui/material/styles";
import * as tokens from "@shape-ds/tokens";

import { baseTheme } from "./base";
import { tooltipClasses } from "@mui/material";

const materialTheme = createTheme(
  {
    palette: { mode: "dark", primary: { main: tokens.darkColors.BLUE_2 } },
    components: {
      MuiAlert: {
        variants: [
          { props: { severity: "success" }, style: { background: tokens.darkColors.GREEN_1 } },
          { props: { severity: "error" }, style: { background: tokens.darkColors.RED_1 } },
          { props: { severity: "info" }, style: { background: tokens.darkColors.BLUE_1 } },
          { props: { severity: "warning" }, style: { background: tokens.darkColors.YELLOW_1 } },
        ],
        styleOverrides: {
          root: { color: tokens.darkColors.GRAY_7 },
        },
      },
      MuiButton: {
        defaultProps: { sx: { fontWeight: 600, minWidth: "120px" } },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              color: tokens.darkColors.GRAY_7,
              backgroundColor: tokens.darkColors.BLUE_2,
              fontFamily: tokens.TYPOGRAPHY_FAMILY_ROBOTO,
              fontWeight: tokens.TYPOGRAPHY_FONT_WEIGHT_BOLD,
              "&:hover": { backgroundColor: tokens.darkColors.BLUE_3 },
              "&:disabled": {
                color: tokens.darkColors.GRAY_6,
                backgroundColor: tokens.darkColors.GRAY_4,
              },
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              color: tokens.darkColors.BLUE_1,
              borderColor: tokens.darkColors.BLUE_1,
              backgroundColor: "unset",
              "&:hover": {
                color: tokens.darkColors.BLUE_2,
                borderColor: tokens.darkColors.BLUE_2,
                backgroundColor: "unset",
              },
              "&:disabled": {
                color: tokens.darkColors.GRAY_5,
                borderColor: tokens.darkColors.GRAY_5,
                backgroundColor: "unset",
              },
            },
          },
          {
            props: { variant: "text" },
            style: {
              color: tokens.darkColors.BLUE_1,
              backgroundColor: "transparent",
              "&:hover": {
                color: tokens.darkColors.BLUE_3,
                backgroundColor: "unset",
              },
              "&:disabled": {
                color: tokens.darkColors.GRAY_5,
                backgroundColor: "unset",
              },
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.darkColors.GRAY_1,
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            sx: {
              background: tokens.darkColors.GRAY_1,
            },
          },
        },
      },
      MuiDialogContent: {
        defaultProps: {
          sx: {
            color: tokens.darkColors.GRAY_7,
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          sx: {
            color: tokens.darkColors.GRAY_7,
            "& .MuiIconButton-root": { color: tokens.darkColors.GRAY_5 },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            color: tokens.darkColors.GRAY_7,
          },
        },
      },
      MuiFormLabel: {
        defaultProps: {
          sx: { "&.Mui-focused": { color: tokens.darkColors.GRAY_7 } },
        },
        styleOverrides: {
          root: {
            color: tokens.darkColors.GRAY_7,
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          sx: {
            "&.Mui-focused": { border: `1px solid ${tokens.darkColors.BLUE_2}` },
            border: `1px solid ${tokens.darkColors.GRAY_5}`,
          },
        },
        variants: [
          {
            props: { error: true },
            style: { borderColor: `${tokens.darkColors.RED_1} !important` },
          },
          {
            props: { disabled: true },
            style: { background: tokens.darkColors.GRAY_3 },
          },
        ],
      },
      MuiRadio: {
        defaultProps: {
          sx: {
            color: tokens.darkColors.GRAY_7,
            "&.Mui-disabled": { color: tokens.darkColors.GRAY_4 },
            "&.Mui-checked": { color: tokens.darkColors.BLUE_2 },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-disabled + .MuiSwitch-track": {
              backgroundColor: tokens.darkColors.GRAY_4,
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: tokens.darkColors.GRAY_3,
            },
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          sx: {
            "& .MuiTab-root": {
              textTransform: "unset",
              fontSize: tokens.TYPOGRAPHY_SIZE_TITLE_XS,
              lineHeight: tokens.TYPOGRAPHY_LINE_HEIGHT_PARAGRAPH_XS,
              color: tokens.darkColors.GRAY_6,
            },
            "& .MuiTabs-indicator": {
              background: tokens.darkColors.GRAY_6,
              borderRadius: "8px",
              height: "4px",
            },
            "& .Mui-selected": {
              color: `${tokens.darkColors.GRAY_7} !important`,
            },
            "& .Mui-disabled": {
              color: `${tokens.darkColors.GRAY_4} !important`,
              svg: { fill: `${tokens.darkColors.GRAY_4} !important` },
            },
          },
        },
        styleOverrides: {
          root: {
            button: {
              minWidth: 60,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiButtonBase-root": { color: tokens.darkColors.GRAY_6 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: tokens.darkColors.GRAY_4,
            color: tokens.darkColors.GRAY_7,
            boxShadow: `0px 1px 5px 0px ${tokens.darkColors.GRAY_3}`,
            [`& .${tooltipClasses.arrow}`]: {
              color: tokens.darkColors.GRAY_4,
            },
          },
        },
      },
    },
    shapeTheme: "dark",
    tokens: {
      colors: {
        blue: {
          "1": tokens.darkColors.BLUE_1,
          "2": tokens.darkColors.BLUE_2,
          "3": tokens.darkColors.BLUE_3,
          "4": tokens.darkColors.BLUE_4,
        },
        gray: {
          "1": tokens.darkColors.GRAY_1,
          "2": tokens.darkColors.GRAY_2,
          "3": tokens.darkColors.GRAY_3,
          "4": tokens.darkColors.GRAY_4,
          "5": tokens.darkColors.GRAY_5,
          "6": tokens.darkColors.GRAY_6,
          "7": tokens.darkColors.GRAY_7,
        },
        green: {
          "1": tokens.darkColors.GREEN_1,
          "2": tokens.darkColors.GREEN_2,
          "3": tokens.darkColors.GREEN_3,
        },
        pink: {
          "1": tokens.darkColors.PINK_1,
          "2": tokens.darkColors.PINK_2,
        },
        red: {
          "1": tokens.darkColors.RED_1,
          "2": tokens.darkColors.RED_2,
          "3": tokens.darkColors.RED_3,
        },
        yellow: {
          "1": tokens.darkColors.YELLOW_1,
          "2": tokens.darkColors.YELLOW_2,
        },
      },
    },
  },
  baseTheme
);

export const darkTheme = materialTheme;
